import React from 'react';
import { Box, makeStyles, Typography, useTheme } from 'housecall-ui';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import { formatMoney } from '../../utils/formatMoney';
import { QuantityInput } from '../QuantityInput';

type PricingFormProps = {
  title: string;
  price: number;
  description: string;
};

type ServiceProps = {
  title: string;
  price: number;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  onDelete: () => void;
  isLastCartItem: boolean;
};

type Props = ServiceProps | PricingFormProps;

const useStyles = makeStyles((theme: HCPTheme) => ({
  lineItem: {
    margin: `${theme.spacing(0.5)}px 0`,
    height: 'auto',
  },
  name: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 'normal',
    [theme.breakpoints.down('md')]: {
      whiteSpace: 'normal',
    },
  },
  titleItem: {
    minWidth: 0,
    flex: '0 1 auto',
  },
  priceItem: {
    minWidth: 0,
    flex: '0 0 auto',
  },
  priceText: {
    minWidth: theme.spacing(10),
    textAlign: 'right',
    [theme.breakpoints.down('md')]: {
      minWidth: theme.spacing(7),
    },
  },
}));

const MAX_VALUE = 10000;

export const CartLineItem = ({ title, price, ...rest }: Props) => {
  const quantity = 'quantity' in rest ? rest.quantity : undefined;
  const description = 'description' in rest ? rest.description : undefined;
  const onQuantityChange = 'onQuantityChange' in rest ? rest.onQuantityChange : undefined;
  const onDelete = 'onDelete' in rest ? rest.onDelete : undefined;
  const isLastCartItem = 'isLastCartItem' in rest ? rest.isLastCartItem : false;

  const { lineItem, name, titleItem, priceItem, priceText } = useStyles();

  const theme = useTheme();
  const isMobile = theme.breakpoints.down('md');

  return (
    <div className={lineItem}>
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" alignItems="center" mr={isMobile ? 0.5 : 1} className={titleItem}>
          <Typography className={name} variant="body2">
            {quantity && quantity > 1 ? `${quantity}*` : ''}
            {title}
          </Typography>
        </Box>
        <Box ml={isMobile ? 0 : 1} display="flex" alignItems="center" className={priceItem}>
          {onQuantityChange && (
            <Box mx={isMobile ? 0.5 : 2}>
              <QuantityInput
                hasDelete
                onChange={onQuantityChange}
                initialValue={quantity || 1}
                onDelete={onDelete}
                maxValue={MAX_VALUE}
                disableDelete={isLastCartItem}
              />
            </Box>
          )}
          {typeof price !== 'undefined' && (
            <Typography data-testid="service-price" className={priceText} variant="body2">
              {formatMoney((quantity ?? 1) * price)}
            </Typography>
          )}
        </Box>
      </Box>
      {description && (
        <Box mt={1}>
          <Typography className={name} variant="body2">
            {description}
          </Typography>
        </Box>
      )}
    </div>
  );
};
